import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaArrowUp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTheme } from "styled-components";
import { action } from "typesafe-actions";
import { commonApi } from "../../../api/talent/common-service";
import { Button } from "../../../components/common";
import Input from "../../../components/form/Input/input";
import LoadingPage from "../../../components/form/loading-pages/loading-pages";
import RangeInput from "../../../components/form/range-input/range-input";
import SelectTag from "../../../components/form/select-tag/select-tag";
import Select from "../../../components/form/select/select";
import { useFilterContext } from "../../../contexts/filter";
import { useQuery } from "../../../hooks";
import City from "../../../models/common/city.model";
import Proficiency from "../../../models/common/proficiency.model";
import { Tag, TagOption } from "../../../models/common/tag.model";
import Ecosystem from "../../../models/ecosystem.model";
import HomeDashboardPageQuery from "../../../models/home-dashboard-page-query.model";
import { AppState } from "../../../store/app-state";
import {
  fetchCandidateCareer,
  fetchTags,
  fetchTagsById,
} from "../../../store/candidate/candidate-actions";
import { CandidateActionTypes } from "../../../store/candidate/candidate-types";
import { fetchCities } from "../../../store/common/common-actions";
import { CommonActionTypes } from "../../../store/common/common-types";
import { fetchSetVisible } from "../../../store/load/load-actions";
import {
  DEFAULT_CURRENCIES,
  DEFAULT_LANGUAGES,
} from "../../../utils/constants";
import { getLocale } from "../../../utils/redux/date";
import { convertIdNameToLabelValue } from "../../../utils/redux/interface-converter";
import { toTitleCase } from "../../../utils/string.utils";
import SelectTagColorful from "../../form/select-tag-colorful";
import { WORK_TYPE } from "../../job-offer/context";
import {
  Actions,
  Checkbox,
  CheckboxContainer,
  CheckboxLabel,
  Container,
  DataList,
  Expand,
  ForcastConclusionContainer,
  Form,
  InputContent,
  Line,
  RadioContainer,
  RadiusInput,
  Range,
  TextFieldContent,
  Title,
  TwoFields,
} from "./filter-form.styled";
import TooltipWarning from "../../tooltip-warning";

interface FilterFormProps {
  hireTypes?: [];
  isCandidatePool?: boolean;
  isSelectedsCandidate?: boolean;
  initialMaxSalary: number;
  grid?: boolean;
  isTrack?: boolean;
  reset?: boolean;
  searchCallback: (query: any) => void;
  disableKeyboard?: boolean;
}

const FilterForm: React.FC<FilterFormProps> = ({
  initialMaxSalary,
  isSelectedsCandidate,
  isCandidatePool,
  grid,
  isTrack,
  searchCallback,
  disableKeyboard,
}) => {
  const { t, i18n } = useTranslation("private-home-page");
  const dispatch = useDispatch();
  const [query, setQuery] = useQuery<HomeDashboardPageQuery>();
  const [filters, setFilters] = useState<any>(null);
  const { isVisible } = useSelector((state: AppState) => state.loadState);
  const { isChanged } = useSelector((state: AppState) => state.tabChangedState);
  const { showNps } = useSelector((state: AppState) => state.npsState);
  const [locationKeys, setLocationKeys] = useState([""]);
  const history = useHistory();
  const theme = useTheme();
  const { data: tagOptions } = useSelector(
    (state: AppState) => state.candidateState.tagsData
  );
  const [selectedCities, setSelectedCities] = useState<City[]>([]);
  const [selectedTag, setSelectedTag] = useState<TagOption[]>([]);
  const [expanded, setExpanded] = useState(false);
  const { data: cities } = useSelector(
    (store: AppState) => store.commonState.cityData
  );

  const { data: careers } = useSelector(
    (state: AppState) => state.candidateState.candidateCareerData
  );

  const { data: proficiencyData } = useSelector(
    (store: AppState) => store.commonState.proficiencyData
  );

  const { data: diversityData } = useSelector(
    (state: AppState) => state.commonState.diversityData
  );

  const { data: experienceTimeData } = useSelector(
    (state: AppState) => state.commonState.experienceTimeData
  );

  const { data: hireTypeData } = useSelector(
    (state: AppState) => state.commonState.hireTypeData
  );

  const { data: skillData } = useSelector(
    (state: AppState) => state.commonState.skillData
  );

  const { data: degrees } = useSelector(
    (state: AppState) => state.commonState.degreeData
  );

  const currencies = useMemo(() => {
    return DEFAULT_CURRENCIES;
  }, []);

  const diversity = useMemo(() => {
    return diversityData?.map((d) => convertIdNameToLabelValue(d));
  }, [diversityData]);

  const experience = useMemo(() => {
    return experienceTimeData.map((e) => convertIdNameToLabelValue(e));
  }, [experienceTimeData]);

  const hireType = useMemo(() => {
    return hireTypeData.map((h) => convertIdNameToLabelValue(h));
  }, [hireTypeData]);

  const degreeType = useMemo(() => {
    return degrees?.map((d) => convertIdNameToLabelValue(d));
  }, [degrees]);

  const skills = useMemo(() => {
    return skillData.map((s) => convertIdNameToLabelValue(s));
  }, [skillData]);

  const [months, years] = useMemo(() => {
    const dateObj = new Date();

    const months = Array.from(Array(12).keys()).map((m) => {
      dateObj.setMonth(m);
      return {
        label: toTitleCase(
          format(dateObj, "MMMM", { locale: getLocale(i18n.language) })
        ),
        value: `${m + 1}`,
      };
    });

    const years = Array.from(Array(10).keys()).map((y) => {
      const year = dateObj.getFullYear() + y;
      return {
        label: year.toString(),
        value: year.toString(),
      };
    });

    return [months, years];
  }, [i18n.language]);

  const currentForecastMonth = useMemo(() => {
    return months.find((m) => m.value === filters?.forecastMonth);
  }, [months, filters?.forecastMonth]);

  const currentForecastYear = useMemo(() => {
    return years.find((y) => y.value === filters?.forecastYear);
  }, [years, filters?.forecastYear]);

  const {
    changeFilter,
    filters: localFilter,
    resetFilters,
  } = useFilterContext();

  useEffect(() => {
    return history.listen((location) => {
      if (history.action === "PUSH") {
        if (location.key) setLocationKeys([location.key]);
        resetFilters && resetFilters();
      }

      if (history.action === "POP") {
        if (locationKeys[1] === location.key) {
          setLocationKeys(([_, ...keys]) => keys);
          setFilters(null);
        } else {
          setLocationKeys((keys: any) => [location.key, ...keys]);
          setFilters(null);
        }
      }
    });
  }, [locationKeys, history, resetFilters]);

  useEffect(() => {
    if (filters?.tags && !tagOptions) {
      const tags = Array.isArray(filters.tags) ? filters.tags : [filters.tags];
      dispatch(fetchTagsById(tags));
    }
  }, [dispatch, filters?.tags, tagOptions]);

  useEffect(() => {
    if (query.workplace) {
      commonApi.fetchCityById(query.workplace).then((res) => {
        setSelectedCities([res.data]);
      });
    }
  }, [query.workplace]);

  const getTagOption = useCallback((tagId: string, tagOptions: Tag[]) => {
    const tag = tagOptions.find((tag) => tag.id === tagId);
    if (!tag) return null;
    return {
      value: tag.id,
      label: tag.name,
      color: tag.color,
    } as TagOption;
  }, []);

  useEffect(() => {
    if (filters?.tags && selectedTag.length === 0 && tagOptions) {
      if (Array.isArray(filters.tags)) {
        const tags = filters.tags
          .map((t: string) => {
            return getTagOption(t, tagOptions);
          })
          .filter((t: TagOption) => t) as TagOption[];
        setSelectedTag(tags);
      } else {
        const tag = getTagOption(filters.tags, tagOptions);
        if (tag) {
          setSelectedTag([tag]);
        }
      }
    }
  }, [filters?.tags, tagOptions, selectedTag, getTagOption]);

  const workType = useMemo(
    () => [
      {
        value: WORK_TYPE.REMOTE,
        label: t("private-home-page:filterForm.acceptRemoteWork"),
      },
      {
        value: WORK_TYPE.PRESENTIAL,
        label: t("private-home-page:filterForm.acceptPresentialWork"),
      },
      {
        value: WORK_TYPE.HYBRID,
        label: t("private-home-page:filterForm.acceptHybridWork"),
      },
    ],
    [t]
  );

  const languages = useMemo(
    () =>
      DEFAULT_LANGUAGES.map((l) => ({
        value: l.value,
        name: t(l.label),
      })),
    [t]
  );

  const knowledgeAreas = useMemo(
    () => [
      {
        value: "1",
        name: t("private-home-page:filterForm.knowledgeAreas.exactSciences"),
      },
      {
        value: "2",
        name: t(
          "private-home-page:filterForm.knowledgeAreas.biologicalSciences"
        ),
      },
      {
        value: "3",
        name: t("private-home-page:filterForm.knowledgeAreas.humanSciences"),
      },
    ],
    [t]
  );

  const filteredTagOptions = useMemo(() => {
    return tagOptions?.map((tag) => ({
      value: tag.id.toString(),
      label: tag.name,
      color: tag.color,
    }));
  }, [tagOptions]);

  const handleSetQuery = useCallback(() => {
    let cleanedFilters = { ...filters };
    delete cleanedFilters.forceTour;
    delete cleanedFilters.onboarding;
    delete cleanedFilters.tour;
    dispatch(fetchSetVisible());
    let finalQuery = { ...cleanedFilters };
    if (isTrack) {
      finalQuery = {
        ...finalQuery,
        pageSize: query.pageSize,
        orderBy: query?.orderBy,
      };
    } else {
      finalQuery = { ...finalQuery, selectedCandidates: true };
    }
    setQuery(finalQuery);
    searchCallback(finalQuery);
  }, [
    filters,
    dispatch,
    query.pageSize,
    query?.orderBy,
    isTrack,
    setQuery,
    searchCallback,
  ]);

  useEffect(() => {
    if (query) {
      const newFilters = {
        ...query,
      };
      setFilters({ ...query, ...newFilters });
    }
  }, [query]);

  const handleChangeFilter = useCallback(
    (filter) => {
      for (const key of Object.keys(filter)) {
        if (filter[key] === "" || !filter[key]) {
          delete filter[key];
        }
      }
      setFilters(filter);
    },
    [setFilters]
  );

  const isPerformance = useMemo(() => query?.orderBy === "performance", [
    query?.orderBy,
  ]);

  const noCoreTechnology = useMemo(
    () => ({
      id: "",
      name: t("private-home-page:filterForm.selectTechnology"),
    }),
    [t]
  );

  const currentCareer = useMemo(() => {
    const current = filters?.career
      ? careers?.find((c) => c.id === filters?.career)
      : null;
    return current;
  }, [careers, filters]);

  const currentCurrency = useMemo(() => {
    const actualCurrency = currencies?.find(
      (c) => c.value.toLowerCase() === filters?.currency?.toLowerCase()
    );
    return actualCurrency;
  }, [currencies, filters]);

  const currentLanguage = useMemo(() => {
    const actualLanguage = languages?.find(
      (c) => c.value.toLowerCase() === filters?.language_id?.toLowerCase()
    );
    return actualLanguage;
  }, [languages, filters]);

  const currentKnowledgeArea = useMemo(() => {
    const actualKnowledgeArea = knowledgeAreas?.find(
      (c) => c.value.toLowerCase() === filters?.knowledge_area?.toLowerCase()
    );
    return actualKnowledgeArea;
  }, [knowledgeAreas, filters]);

  const user = useSelector((state: AppState) => state.authState.user);

  const defaultCorpoateCurrency = useMemo(() => {
    return currencies?.find(
      (c) => c.value.toLowerCase() === user?.corporate?.currency?.toLowerCase()
    );
  }, [user, currencies]);

  const ecosystems = useMemo<Ecosystem[]>(() => {
    return currentCareer?.ecosystems ? currentCareer?.ecosystems : [];
  }, [currentCareer]);

  const currentEcosystem = useMemo(() => {
    return ecosystems?.find((e) => e.id === filters?.ecosystem);
  }, [ecosystems, filters]);

  const handleResetForm = useCallback(() => {
    const currency = { currency: defaultCorpoateCurrency?.value };
    if (isTrack) {
      setQuery({ ...currency, orderBy: query?.orderBy });
    } else {
      setQuery({ ...currency, selectedCandidates: "true" });
    }
    setTimeout(() => {
      setFilters({ currency: defaultCorpoateCurrency?.value });
      setSelectedCities([]);
      setSelectedTag([]);
    }, 1000);
  }, [
    setFilters,
    setSelectedCities,
    setQuery,
    query,
    isTrack,
    defaultCorpoateCurrency,
  ]);

  const handleSearchTag = useCallback(
    (input: string) => {
      if (input.length) {
        dispatch(fetchTags(input));
      } else {
        dispatch(action(CandidateActionTypes.FETCH_TAGS_SUCCESS, []));
      }
    },
    [dispatch]
  );

  const getTagValues = (filter: any, tags: any) => {
    if (filter) {
      if (typeof filter !== "string") {
        return (
          filter?.map((v: any) => tags?.find((t: any) => t.value === v)) || []
        );
      } else {
        return tags?.find((t: any) => t.value === filter);
      }
    }
    return null;
  };

  const proficiencyValue = useMemo((): Proficiency | undefined => {
    if (typeof filters?.language_level !== "string") {
      return filters?.language_level
        ?.map((e: string) => proficiencyData.find((en) => en.key === e))
        .sort((e: any) => e.order)[0];
    }
    return proficiencyData.find((e) => e.key === filters?.language_level);
  }, [proficiencyData, filters]);

  const checkVisible = useCallback(
    (field: string) => {
      if (isSelectedsCandidate) {
        return (
          expanded || ["name", "hireType", "english"].some((f) => f === field)
        );
      }
      if (isCandidatePool) {
        return (
          expanded || ["skill", "career", "ecosystem"].some((f) => f === field)
        );
      }
      if (isTrack) {
        return (
          expanded || ["name", "career", "ecosystem"].some((f) => f === field)
        );
      }

      return (
        expanded || ["name", "hireType", "english"].some((f) => f === field)
      );
    },
    [expanded, isCandidatePool, isTrack, isSelectedsCandidate]
  );

  const setProficiencyValue = useCallback(
    (newValue: Proficiency) => {
      if (newValue.order === 0) {
        setFilters((f: any) => ({ ...f, language_level: undefined }));
        changeFilter({ ...localFilter, language_level: undefined });
        return;
      }
      const options = proficiencyData
        .filter((e) => e.order >= newValue.order)
        .map((e) => e.key);
      setFilters((f: any) => ({ ...f, language_level: options }));
      changeFilter({ ...localFilter, language_level: options });
    },
    [proficiencyData, changeFilter, localFilter]
  );

  useEffect(() => {
    setFilters(null);
  }, [isChanged, setFilters]);

  useEffect(() => {
    if (isCandidatePool || isTrack) {
      dispatch(fetchCandidateCareer());
    }
  }, [isCandidatePool, isTrack, dispatch]);

  const handleInputCity = useCallback(
    (input: string) => {
      if (input.length) {
        dispatch(fetchCities(input));
      } else {
        dispatch(action(CommonActionTypes.FETCH_CITY_DATA_SUCCESS, []));
      }
    },
    [dispatch]
  );

  const handleSelectCity = useCallback(
    (obj) => {
      if (obj.length > 0) {
        const lastObj = obj[obj.length - 1];
        const id = parseInt(lastObj.value || "0");
        setSelectedCities([
          {
            label: lastObj.label || "",
            value: lastObj.value || "",
            city: id,
          },
        ]);
        handleChangeFilter({ ...filters, workplace: lastObj.value });
      } else {
        setSelectedCities([]);
        handleChangeFilter({ ...filters, workplace: undefined });
      }
    },
    [filters, handleChangeFilter]
  );

  const handleSelectTag = useCallback(
    (obj) => {
      if (obj.length > 0) {
        const query = obj.map((o: any) => o.value);
        setSelectedTag(obj);
        handleChangeFilter({ ...filters, tags: query });
      } else {
        setSelectedTag([]);
        handleChangeFilter({ ...filters, tags: undefined });
      }
    },
    [filters, handleChangeFilter]
  );

  const setForecastMonth = useCallback(
    (month: string) => {
      const obj: any = { forecastMonth: month };
      !filters?.forecastYear && (obj["forecastYear"] = years[0].value);

      handleChangeFilter({
        ...filters,
        ...obj,
      });
    },
    [years, filters, handleChangeFilter]
  );

  const setForecastYear = useCallback(
    (year: string) => {
      const obj: any = { forecastYear: year };
      !filters?.forecastMonth && (obj["forecastMonth"] = months[0].value);

      handleChangeFilter({
        ...filters,
        ...obj,
      });
    },
    [months, filters, handleChangeFilter]
  );

  const handleKeyPressed = useCallback(
    (event) => {
      const ENTER_KEY_CODE = 13;
      if (!disableKeyboard && !showNps && event.keyCode === ENTER_KEY_CODE) {
        handleSetQuery();
      }
    },
    [handleSetQuery, disableKeyboard, showNps]
  );

  useEffect(() => {
    const EVENT_TYPE = "keydown";
    document.addEventListener(EVENT_TYPE, handleKeyPressed, false);
    return () => {
      document.removeEventListener(EVENT_TYPE, handleKeyPressed, false);
    };
  }, [handleKeyPressed]);

  return (
    <Container>
      <Form onSubmit={(e) => e.preventDefault()} grid={grid}>
        <InputContent>
          {(isSelectedsCandidate || isTrack) && checkVisible("name") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.search")}</Title>
              <Input
                name="search"
                type="text"
                placeholder=""
                value={filters?.search ?? ""}
                debounceDelay={0}
                onDeboncedValueChange={(newValue) => {
                  handleChangeFilter({
                    ...filters,
                    search: `${newValue}`.trim(),
                  });
                  changeFilter({
                    ...localFilter,
                    search: `${newValue}`.trim(),
                  });
                }}
              />
            </TextFieldContent>
          )}
          {(isCandidatePool || isTrack) && checkVisible("career") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.career")}</Title>
              <Select
                placeholder={t("private-home-page:filterForm.selectCareer")}
                values={careers ? careers : []}
                value={currentCareer}
                onChange={(newValue) => {
                  handleChangeFilter({
                    ...filters,
                    career: `${newValue?.id}`,
                    ecosystem: "",
                  });
                  changeFilter({
                    ...localFilter,
                    career: `${newValue?.name}`,
                    ecosystem: "",
                  });
                }}
                getElementLabel={(career) => career?.name || ""}
                onClear={() => {
                  setFilters((f: any) => ({
                    ...f,
                    career: undefined,
                    ecosystem: undefined,
                  }));
                  changeFilter({
                    ...localFilter,
                    career: undefined,
                    ecosystem: undefined,
                  });
                }}
              />
            </TextFieldContent>
          )}
          {((isCandidatePool && ecosystems) || isTrack) &&
            checkVisible("ecosystem") && (
              <TextFieldContent>
                <Title>
                  {t("private-home-page:filterForm.coreTechnologyRequired")}
                </Title>
                <Select
                  placeholder={t(
                    "private-home-page:filterForm.selectCoreTechnology"
                  )}
                  value={currentEcosystem}
                  values={
                    ecosystems
                      ? isPerformance
                        ? [noCoreTechnology, ...ecosystems]
                        : ecosystems
                      : []
                  }
                  onChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      ecosystem: `${newValue.id}`,
                    });
                    changeFilter({
                      ...localFilter,
                      ecosystem: `${newValue.name}`,
                    });
                  }}
                  getElementLabel={(ecosystem) => ecosystem?.name || ""}
                  onClear={() => {
                    setFilters((f: any) => ({
                      ...f,
                      ecosystem: undefined,
                    }));
                    changeFilter({ ...localFilter, ecosystem: undefined });
                  }}
                />
              </TextFieldContent>
            )}
          {((isCandidatePool && ecosystems) || isTrack) &&
            checkVisible("skill") && (
              <TextFieldContent>
                <Title>{t("private-home-page:filterForm.skills")}</Title>
                <SelectTag
                  placeholder={t("private-home-page:filterForm.selectTags")}
                  values={getTagValues(filters?.skills, skills)}
                  options={skills}
                  onChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      skills: newValue.map((v) => v.value),
                    });
                    changeFilter({
                      ...localFilter,
                      skills: newValue.map((v) => v.label),
                    });
                  }}
                />
              </TextFieldContent>
            )}
          {isSelectedsCandidate && checkVisible("hireType") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.hireTypes")}</Title>
              <SelectTag
                placeholder={t("private-home-page:filterForm.selectTags")}
                values={getTagValues(filters?.hireTypes, hireType)}
                options={hireType || []}
                onChange={(newValue) => {
                  handleChangeFilter({
                    ...filters,
                    hireTypes: newValue.map((v) => v.value),
                  });
                  changeFilter({
                    ...localFilter,
                    hireTypes: newValue.map((v) => v.label),
                  });
                }}
              />
            </TextFieldContent>
          )}
          {checkVisible("english") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.english")}</Title>
              <TwoFields>
                <Select
                  value={currentLanguage}
                  values={languages}
                  onChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      language_id: newValue.value,
                    });
                    changeFilter({
                      ...localFilter,
                      language_id: newValue.name,
                    });
                  }}
                  getElementLabel={(e) => e.name}
                  placeholder={t("private-home-page:filterForm.select")}
                  onClear={() => {
                    setFilters((f: any) => ({ ...f, language_id: undefined }));
                    changeFilter({ ...localFilter, language_id: undefined });
                  }}
                />
                <Select
                  value={proficiencyValue}
                  values={proficiencyData}
                  onChange={setProficiencyValue}
                  getElementLabel={(e) => e.name}
                  placeholder={t("private-home-page:filterForm.select")}
                  onClear={() => {
                    setFilters((f: any) => ({
                      ...f,
                      language_level: undefined,
                    }));
                  }}
                />
              </TwoFields>
            </TextFieldContent>
          )}
          {checkVisible("salary") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.salaryRange")}</Title>
              <Range>
                <Select
                  placeholder={t("private-home-page:filterForm.selectCurrency")}
                  values={
                    currencies
                      ? currencies.map((c) => {
                          return {
                            label: `${c.label} ${c.value}`,
                            value: c.value,
                          };
                        })
                      : []
                  }
                  value={currentCurrency}
                  onChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      currency: newValue.value,
                    });
                    changeFilter({ ...localFilter, currency: newValue.label });
                  }}
                  getElementLabel={(c) => c.label}
                  onClear={() => {
                    setFilters((f: any) => ({
                      ...f,
                      currency: undefined,
                    }));
                    changeFilter({ ...localFilter, currency: undefined });
                  }}
                  hidden={true}
                />
                <Line />
                <RangeInput
                  name="minSalary"
                  type="text"
                  placeholder={t("private-home-page:filterForm.minRange")}
                  value={filters?.minSalary ?? ""}
                  debounceDelay={200}
                  onDeboncedValueChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      minSalary: `${newValue ?? ""}`,
                    });
                    changeFilter({
                      ...localFilter,
                      minSalary: `${newValue ?? ""}`,
                    });
                  }}
                  prefix={currentCurrency?.label}
                />
                <Line />
                <RangeInput
                  name="maxSalary"
                  type="text"
                  placeholder={t("private-home-page:filterForm.maxRange")}
                  value={filters?.maxSalary ?? ""}
                  debounceDelay={200}
                  onDeboncedValueChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      maxSalary: `${newValue ?? ""}`,
                    });
                    changeFilter({
                      ...localFilter,
                      maxSalary: `${newValue ?? ""}`,
                    });
                  }}
                  prefix={currentCurrency?.label}
                />
              </Range>
            </TextFieldContent>
          )}
          {checkVisible("professionalExperience") && (
            <TextFieldContent>
              <Title>
                {t("private-home-page:filterForm.professionalExperience")}
              </Title>
              <SelectTag
                placeholder={t("private-home-page:filterForm.selectTags")}
                values={getTagValues(filters?.experience, experience)}
                options={experience}
                onChange={(newValue) => {
                  handleChangeFilter({
                    ...filters,
                    experience: newValue.map((v) => v.value),
                  });
                  changeFilter({
                    ...localFilter,
                    experience: newValue.map((v) => v.label),
                  });
                }}
              />
              {filters?.experience && filters?.experience.length > 0 && (
                <RadioContainer>
                  <label>
                    <input
                      type="radio"
                      name="experience-group"
                      value="true"
                      checked={!filters?.experience_by_ecosystem}
                      onChange={(e) => {
                        handleChangeFilter({
                          ...filters,
                          experience_by_ecosystem: false,
                        });
                      }}
                    />
                    <span>
                      {t(
                        "private-home-page:filterForm.experience-radio.by-experience"
                      )}
                    </span>
                    <TooltipWarning
                      message={t(
                        "private-home-page:filterForm.experience-radio.by-experience-info"
                      )}
                    />
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="experience-group"
                      value="true"
                      checked={!!filters?.experience_by_ecosystem}
                      onChange={(e) => {
                        handleChangeFilter({
                          ...filters,
                          experience_by_ecosystem: true,
                        });
                      }}
                    />
                    <span>
                      {t(
                        "private-home-page:filterForm.experience-radio.by-ecossystem"
                      )}
                    </span>
                    <TooltipWarning
                      message={t(
                        "private-home-page:filterForm.experience-radio.by-ecossystem-info"
                      )}
                    />
                  </label>
                </RadioContainer>
              )}
            </TextFieldContent>
          )}
          {((isCandidatePool && ecosystems) || isTrack) &&
            checkVisible("preferences") && (
              <TextFieldContent>
                <Title>{t("private-home-page:filterForm.preferences")}</Title>
                <SelectTag
                  placeholder={t("private-home-page:filterForm.selectTags")}
                  values={getTagValues(filters?.workType, workType)}
                  options={workType || []}
                  onChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      workType: newValue.map((v) => v.value),
                    });
                    changeFilter({
                      ...localFilter,
                      workType: newValue.map((v) => v.label),
                    });
                  }}
                />
              </TextFieldContent>
            )}
          {((isCandidatePool && ecosystems) || isTrack) &&
            checkVisible("hireType") && (
              <TextFieldContent>
                <Title>{t("private-home-page:filterForm.hireTypes")}</Title>
                <SelectTag
                  placeholder={t("private-home-page:filterForm.selectTags")}
                  values={getTagValues(filters?.hireTypes, hireType)}
                  options={hireType}
                  onChange={(newValue) => {
                    handleChangeFilter({
                      ...filters,
                      hireTypes: newValue.map((v) => v.value),
                    });
                    changeFilter({
                      ...localFilter,
                      hireTypes: newValue.map((v) => v.label),
                    });
                  }}
                />
              </TextFieldContent>
            )}
          {checkVisible("diversity") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.diversity")}</Title>
              <SelectTag
                placeholder={t("private-home-page:filterForm.selectTags")}
                values={getTagValues(filters?.diversity, diversity)}
                options={diversity}
                onChange={(newValue) => {
                  handleChangeFilter({
                    ...filters,
                    diversity: newValue.map((v) => v.value),
                  });
                  changeFilter({
                    ...localFilter,
                    diversity: newValue.map((v) => v.label),
                  });
                }}
              />
            </TextFieldContent>
          )}
          {checkVisible("forecast") && (
            <TextFieldContent>
              <Title>
                {t("private-home-page:filterForm.forecastConclusion")}
              </Title>
              <ForcastConclusionContainer>
                <Select
                  id="forecast"
                  value={currentForecastMonth}
                  values={months}
                  onChange={(newValue) => setForecastMonth(newValue.value)}
                  getElementLabel={(e) => e.label}
                  placeholder={t("private-home-page:filterForm.forecastMonth")}
                  onClear={() => {
                    setFilters((f: any) => ({
                      ...f,
                      forecastMonth: undefined,
                    }));
                  }}
                />
                <Select
                  id="forecast"
                  value={currentForecastYear}
                  values={years}
                  onChange={(newValue) => setForecastYear(newValue.value)}
                  getElementLabel={(e) => e.label}
                  placeholder={t("private-home-page:filterForm.forecastYear")}
                  onClear={() => {
                    setFilters((f: any) => ({ ...f, forecastYear: undefined }));
                  }}
                />
              </ForcastConclusionContainer>
            </TextFieldContent>
          )}

          {isSelectedsCandidate && checkVisible("tags") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.tags")}</Title>
              <SelectTagColorful
                placeholder={t("private-home-page:filterForm.selectTags")}
                values={selectedTag}
                options={filteredTagOptions || []}
                onChange={(newValue) => {
                  handleSelectTag(newValue);
                }}
                onInputChange={(input) => handleSearchTag(input)}
              />
            </TextFieldContent>
          )}
          {checkVisible("knowledgeArea") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.knowledgeArea")}</Title>
              <Select
                placeholder={t("private-home-page:filterForm.select")}
                value={currentKnowledgeArea}
                values={knowledgeAreas}
                onChange={(newValue) =>
                  handleChangeFilter({
                    ...filters,
                    knowledge_area: `${newValue.value}`,
                  })
                }
                getElementLabel={(knowledgeArea) => knowledgeArea?.name || ""}
                onClear={() => {
                  setFilters((f: any) => ({
                    ...f,
                    knowledge_area: undefined,
                  }));
                }}
              />
            </TextFieldContent>
          )}

          {checkVisible("degree") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.degree")}</Title>
              <SelectTag
                placeholder={t("private-home-page:filterForm.selectTags")}
                values={getTagValues(filters?.level, degreeType)}
                options={degreeType}
                onChange={(degree) => {
                  handleChangeFilter({
                    ...filters,
                    level: degree.map((v) => v.value),
                  });
                }}
              />
            </TextFieldContent>
          )}
          {checkVisible("city") && (
            <TextFieldContent>
              <Title>{t("private-home-page:filterForm.city")}</Title>
              <SelectTag
                placeholder={t("private-home-page:filterForm.city")}
                values={selectedCities}
                options={cities}
                onChange={(selectedValues) => {
                  handleSelectCity(selectedValues);
                  changeFilter({
                    ...localFilter,
                    workplace: selectedValues.map((v) => v.label),
                  });
                }}
                onInputChange={(input) => handleInputCity(input)}
              />
              {selectedCities && selectedCities.length > 0 && (
                <CheckboxContainer>
                  <Checkbox
                    name="move_location"
                    type="checkbox"
                    placeholder=""
                    onChange={(e) => {
                      handleChangeFilter({
                        ...filters,
                        move_location: e.currentTarget.checked,
                      });
                    }}
                  />{" "}
                  <CheckboxLabel>
                    {t("private-home-page:filterForm.moveLocation", {
                      city: selectedCities[0]?.label,
                    })}
                  </CheckboxLabel>
                </CheckboxContainer>
              )}
              {selectedCities && selectedCities.length > 0 && (
                <TextFieldContent>
                  <CheckboxLabel>
                    {t("private-home-page:filterForm.radius")}
                  </CheckboxLabel>
                  <div>
                    <RadiusInput
                      name="radius"
                      type="range"
                      placeholder=""
                      max={100}
                      min={0}
                      list="markers"
                      step={25}
                      value={filters?.radius ?? "0"}
                      onChange={(e) => {
                        handleChangeFilter({
                          ...filters,
                          radius: e.currentTarget.value,
                        });
                      }}
                    />

                    <DataList id="markers">
                      <option
                        value="0"
                        label={t("private-home-page:filterForm.radiusNone")}
                      >
                        {" "}
                      </option>
                      <option value="25" label="25 km">
                        {" "}
                      </option>
                      <option value="50" label="50 km">
                        {" "}
                      </option>
                      <option value="75" label="75 km">
                        {" "}
                      </option>
                      <option value="100" label="100 km">
                        {" "}
                      </option>
                    </DataList>
                  </div>
                </TextFieldContent>
              )}
            </TextFieldContent>
          )}
        </InputContent>
      </Form>

      <Actions>
        <Button
          textColor={theme.colors.primaryBlue.text}
          backgroundColor={theme.colors.primaryBlue.main}
          onClick={() => handleResetForm()}
          bordered={true}
          width="10rem"
        >
          {t("private-home-page:filterForm.clean")}
        </Button>

        <Button
          onClick={() => handleSetQuery()}
          disabled={isCandidatePool && !filters?.ecosystem}
          textColor={theme.colors.primaryBlue.text}
          backgroundColor={theme.colors.primaryBlue.main}
        >
          {t("private-home-page:filterForm.filter")}
        </Button>
      </Actions>

      {isVisible.data && isCandidatePool && (
        <LoadingPage
          messages={[
            t("private-home-page:filterForm.loadingMessage1"),
            t("private-home-page:filterForm.loadingMessage2"),
            t("private-home-page:filterForm.loadingMessage3"),
            t("private-home-page:filterForm.loadingMessage4"),
            t("private-home-page:filterForm.loadingMessage5"),
            t("private-home-page:filterForm.loadingMessage6"),
          ]}
        />
      )}

      {isVisible.data && (isTrack || isSelectedsCandidate) && (
        <LoadingPage
          messages={[t("private-home-page:filterForm.loadingMessage7")]}
        />
      )}
      <Expand
        onClick={() => {
          setExpanded(!expanded);
        }}
        rotate={expanded}
      >
        <FaArrowUp size={40} color={theme.colors.primaryBlue.main} />
      </Expand>
    </Container>
  );
};

export default FilterForm;
